import { useMutation } from '@tanstack/react-query';
import { resetWithdrawalTransaction, resetStuckWithdrawalTransactions } from '../api/withdrawal';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

/**
 * Hook for resetting a withdrawal transaction
 * @returns Mutation for resetting a withdrawal transaction
 */
export const useResetWithdrawalTransaction = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => resetWithdrawalTransaction(id),
    onSuccess: (data) => {
      if (data.status) {
        showSuccessNotification(
          t('withdrawal:resetTransaction.success', 'Transaction Reset'),
          t('withdrawal:resetTransaction.successMessage', 'Transaction has been reset successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal:resetTransaction.error', 'Reset Failed'),
          data.error || t('withdrawal:resetTransaction.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:resetTransaction.error', 'Reset Failed'),
        error?.message || t('withdrawal:resetTransaction.unknownError', 'An unknown error occurred')
      );
    }
  });
};

/**
 * Hook for resetting stuck withdrawal transactions
 * @returns Mutation for resetting stuck withdrawal transactions
 */
export const useResetStuckWithdrawalTransactions = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (options: { olderThanMinutes?: number; statuses?: string[] } = {}) =>
      resetStuckWithdrawalTransactions(options),
    onSuccess: (data) => {
      if (data.status) {
        const count = data.data?.modifiedCount || 0;
        // Use a simpler approach to avoid TypeScript errors
        const successMessage = `Reset ${count} stuck transactions`;
        showSuccessNotification(
          t('withdrawal:resetStuckTransactions.success', 'Transactions Reset'),
          successMessage
        );
      } else {
        showErrorNotification(
          t('withdrawal:resetStuckTransactions.error', 'Reset Failed'),
          data.error || t('withdrawal:resetStuckTransactions.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:resetStuckTransactions.error', 'Reset Failed'),
        error?.message || t('withdrawal:resetStuckTransactions.unknownError', 'An unknown error occurred')
      );
    }
  });
};
